import React from 'react';
import { Button, Card, Flex, theme } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

import { useCfFlowEventTracker } from '../useCfFlowEventTracker';
import CloudflareNameserverPair from './CloudflareNameserverPair';
import CloudflareStepHeader from './CloudflareStepHeader';

export const CF_NAMESERVER_EVENTS = {
  confirmed: 'Automated CF Integration: Nameservers Confirmed',
} as const;

export type CloudflareNameserverStepProps = {
  domain: string;
  nameservers: readonly string[];
  isChecking?: boolean;
  onConfirm?: () => void;
  onGoBack?: () => void;
};

/**
 * The second manual step for a domain not on Cloudflare.
 *
 * The customer changes their nameservers at their registrar, which is the one
 * part of this flow nobody can do for them — and the one with the longest wait
 * behind it. Confirming here runs the immediate check: a zone
 * that is already active skips the wait entirely.
 */
const CloudflareNameserverStep = ({
  domain,
  nameservers,
  isChecking = false,
  onConfirm,
  onGoBack,
}: CloudflareNameserverStepProps) => {
  const { token } = theme.useToken();
  const track = useCfFlowEventTracker({ domain });

  const onConfirmClick = () => {
    if (isChecking) return;

    track(CF_NAMESERVER_EVENTS.confirmed);
    onConfirm?.();
  };

  return (
    <Flex vertical gap={token.marginLG}>
      <CloudflareStepHeader
        title="Point your domain to Cloudflare"
        description="Sign in to the company you bought your domain from, find the nameserver setting, and replace what's there with the two addresses below, that's what sends your site's traffic through Cloudflare."
      />

      <Card styles={{ body: { padding: token.paddingXL } }}>
        <CloudflareNameserverPair nameservers={nameservers} withRegistrarInstruction />
      </Card>

      <Flex justify="space-between" align="center">
        {onGoBack ? (
          <Button type="link" size="large" icon={<ArrowLeftOutlined />} onClick={onGoBack}>
            Back
          </Button>
        ) : (
          <span />
        )}
        {/* Available even when the fetch gave nothing: the customer can read
            their nameservers from Cloudflare and still move on. */}
        <Button type="primary" size="large" onClick={onConfirmClick} loading={isChecking}>
          I updated nameservers, continue
        </Button>
      </Flex>
    </Flex>
  );
};

export default CloudflareNameserverStep;
